var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var MovingAverageForecastConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { BeforeDeserialized, JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ForecastFrequency } from './baseForecastConfig';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
const schema = {
    $id: 'urn:wisepipe-common-pipe:MovingAverageForecastConfig',
    type: 'object',
    properties: {
        dateColumn: { type: 'string', minLength: 1 },
        valueColumn: { type: 'string', minLength: 1 },
        alias: { type: 'string', minLength: 1 },
        window: { type: 'integer', minimum: 3 },
        periods: { type: 'integer', minimum: 1 },
        frequency: {
            type: 'string',
            enum: Object.values(ForecastFrequency)
        },
        groupByColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        }
    },
    required: ['dateColumn', 'valueColumn', 'alias', 'window', 'periods']
};
let MovingAverageForecastConfig = MovingAverageForecastConfig_1 = class MovingAverageForecastConfig extends ToolConfig {
    constructor(dateColumn, valueColumn, alias, window, periods, frequency, groupByColumns, customFormats) {
        super(ToolType.MOVING_AVERAGE_FORECAST, customFormats);
        this.dateColumn = dateColumn;
        this.valueColumn = valueColumn;
        this.alias = alias;
        this.window = window;
        this.periods = periods;
        this.frequency = frequency;
        if (groupByColumns !== undefined)
            this.groupByColumns = groupByColumns;
        JSON.validate(this, MovingAverageForecastConfig_1);
    }
    setDefaults() {
        this.frequency = ForecastFrequency.BestGuess;
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return this.ins[0];
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return MovingAverageForecastConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return MovingAverageForecastConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return MovingAverageForecastConfig_1.outs;
    }
    static get in() {
        return MovingAverageForecastConfig_1.ins[0];
    }
    static get out() {
        return MovingAverageForecastConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.MOVING_AVERAGE_FORECAST.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.UNSEEN, ToolCategories.FORECAST], this.ins, this.outs, ['moving average', 'forecast'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        // Moving Average is kept as old schema for now, will update later
        if (source) {
            this.getExportsForMovingAverageForecastConfig(tsb, this.in, this.out, this.dateColumn, this.valueColumn, this.alias, this.groupByColumns);
        }
        return tsb.build();
    }
    getExportsForMovingAverageForecastConfig(tsb, inPort, outPort, dateColumn, valueColumn, alias, groupByColumns) {
        const exp = tsb.export(outPort);
        const newColumnFormats = new Map();
        // calculate dependent parent columns.  forecast only "uses" date + val, but you can grp by val so add those too
        const dependents = [];
        dependents.push(exp.findSourceColumn(inPort, dateColumn));
        dependents.push(exp.findSourceColumn(inPort, valueColumn));
        dependents.push(...this.getGroupByDependents(exp, inPort, groupByColumns, dependents));
        exp.makeNew(new Column(dateColumn, 'date'), dependents, true);
        let srcColumn = exp.findSourceColumn(inPort, dateColumn);
        if (srcColumn.customFormat) {
            newColumnFormats.set(dateColumn, srcColumn.customFormat.copyWithInheritedTrue());
        }
        exp.makeNew(new Column(alias, 'number'), dependents, true);
        srcColumn = exp.findSourceColumn(inPort, valueColumn);
        if (srcColumn.customFormat) {
            newColumnFormats.set(alias, srcColumn.customFormat.copyWithAutoGeneratedTrue());
        }
        exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        return exp;
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationState.BAD_METADATA;
        }
        if (this.dateColumn === this.valueColumn) {
            return ToolValidationState.BAD_METADATA;
        }
        const outputs = this.groupByColumns ? [...this.groupByColumns] : [];
        outputs.push(this.dateColumn, this.alias);
        if (new Set(outputs).size !== outputs.length) {
            return ToolValidationState.BAD_METADATA;
        }
        const inputs = new Map([
            [this.dateColumn, ['date']],
            [this.valueColumn, ['number']]
        ]);
        this.addGroupByToInputs(inputs, this.groupByColumns);
        return isSourceValid(source, inputs, []) ? ToolValidationState.VALID : ToolValidationState.BAD_METADATA;
    }
};
__decorate([
    BeforeDeserialized()
], MovingAverageForecastConfig.prototype, "setDefaults", null);
MovingAverageForecastConfig = MovingAverageForecastConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.MOVING_AVERAGE_FORECAST)
], MovingAverageForecastConfig);
export { MovingAverageForecastConfig };
//# sourceMappingURL=movingAverageForecastConfig.js.map